import { 
    store, 
    getContext, 
    getElement,
    withScope,
    useState, 
    useEffect
} from '@wordpress/interactivity';


import { STORE_NAMESPACE } from '../modules/default-store';


/**
 * Initializes the interactivity store for block effects.
 *
 * @returns {void}
 */
const { state, callbacks } = store( STORE_NAMESPACE, {
    state: {},
    actions: {
        /**
         * Starts the delayed cursor-processing state after the configured timeout.
         *
         * @param {Event} e - The trigger event.
         * @returns {void}
         */
        startCursorTracking: (e) => {
            const context = /** @type {any} */ ( getContext( STORE_NAMESPACE ) )

            clearTimeout( context?.timer )
            context.timer = setTimeout( () => {
                context.hasProcessing = true
            }, /** @type {{ settings?: { processingDelay?: number } }} */ ( state ).settings?.processingDelay ?? 0 )
        },
        /**
         * Updates the context with the current cursor position and element dimensions.
         * @param {MouseEvent} e - The mouse event containing the pointer position.
         * @returns {void}
         */
        setCursorTracking: (e) => {
            const { clientX = 0, clientY = 0 } = /** @type {MouseEvent} */ ( e )
            const { ref } = /** @type {any} */ ( getElement() )
            const context = /** @type {any} */ ( getContext( STORE_NAMESPACE ) )

            const rect = ref?.getBoundingClientRect?.() ?? { left: 0, top: 0, width: 0, height: 0 }
            const { left, top, width, height } = rect
            const layerX = ( clientX - left ) - width / 2,
                layerY = ( clientY - top ) - height / 2

            context.cursorPosition = {
                x: layerX,
                y: layerY
            }
            context.size = {
                width,
                height
            }
        },
        /**
         * Clears the cursor position and resets the processing state after a short delay.
         *
         * @param {Event} e - The trigger event.
         * @returns {void}
         */
        clearCursorTracking: (e) => {
            const context = /** @type {any} */ ( getContext( STORE_NAMESPACE ) )

            context.cursorPosition = undefined
            context.size = undefined

            clearTimeout( context?.timer ) 
            context.timer = setTimeout( () => {
                context.hasProcessing = false
            }, 20 )
        },
        /**
         * Updates the scroll-based object position for the current element.
         *
         * @returns {void}
         */
        setScrollTracking: () => {
            const context = /** @type {any} */ ( getContext( STORE_NAMESPACE ) )
            const { ref, attributes } = /** @type {any} */ ( getElement() )
            const reverse = attributes?.['data-reverse']


            if ( !context.isVisible )
                return;

            const value = callbacks.getScrollValue( ref, context.limitFactor, reverse !== undefined )

            if ( !!context.direction && null !== value ) {
                context.scrollTracking[context.direction] = value
                context.objectPosition = `${context.scrollTracking.x}% ${context.scrollTracking.y}%`
                context.hasProcessing = true
            }
        }
    },
    callbacks: {
        /**
         * Initializes scroll tracking by configuring parallax parameters and observing visibility changes.
         *
         * @param {Event} e - The trigger event.
         * @returns {Function|undefined} Cleanup function for the observer.
         */
        initScrollTracking: (e) => {
            const { ref } = /** @type {any} */ ( getElement() )
            const context = /** @type {any} */ ( getContext( STORE_NAMESPACE ) )
            const parentRef = ref?.parentElement

            if ( !parentRef )
                return;


            callbacks.setParallaxParams( ref, parentRef )


            const observer = new IntersectionObserver( ( [ entry ], obs ) => {
                context.isVisible = entry.isIntersecting
            }, { rootMargin: '100px 0px 100px 0px' } )

            observer.observe( parentRef )


            return () => ref && observer.unobserve( parentRef ) // && scrollObserver.unobserve( ref )*/
        },
        /**
         * Starts the scroll-based tracking update when the element is visible and not already processing.
         *
         * @returns {void}
         */
        startScrollTracking: () => {
            const { ref, attributes } = /** @type {any} */ ( getElement() )
            const reverse = attributes?.['data-reverse']
            const context = /** @type {any} */ ( getContext( STORE_NAMESPACE ) )

            if ( context.hasProcessing )
                return;


            if ( !context.isVisible ) {
                context.hasProcessing = false

                return;
            }


            const scrollValue = callbacks.getScrollValue( ref, context.limitFactor, reverse !== undefined )

            context.scrollTracking[context.direction] = scrollValue
            context.objectPosition = `${context.scrollTracking.x}% ${context.scrollTracking.y}%`
        },
        /**
         * Calculates the current scroll value for the parallax effect.
         *
         * @param {HTMLElement} ref - The element to measure.
         * @param {number|string} limitFactor - The configured limit factor for the effect.
         * @param {boolean} reverse - Whether the direction should be reversed.
         * @returns {number|null} The computed scroll value or null when no movement is needed.
         */
        getScrollValue: (ref, limitFactor, reverse) => {
            const numericLimitFactor = typeof limitFactor === 'number' ? limitFactor : parseFloat( limitFactor )
            const maxLimit = Number.isFinite( numericLimitFactor ) ? numericLimitFactor : 0
            const { top, height } = ref.getBoundingClientRect()
            const { innerHeight } = window

            const max = height + innerHeight
            const curr = top - innerHeight

            let value = curr * 100 / max * -1


            if ( maxLimit > 0 && maxLimit < 1 ) {
                if ( value > 50 ) {
                    value = 50 + ( value - 50 ) * maxLimit
                } else if ( value < 50 ) {
                    value = 50 - ( 50 - value ) * maxLimit
                }
            }

            return reverse ? 100 - value : value;
        },
        /**
         * Sets the parallax direction and limit factor based on the element and parent dimensions.
         *
         * @param {HTMLElement} ref - The animated element.
         * @param {HTMLElement} parentRef - The parent container used for measurement.
         * @returns {void}
         */
        setParallaxParams: (ref, parentRef) => {
            const context = /** @type {any} */ ( getContext() )
            const imageRef = /** @type {HTMLImageElement} */ ( ref )
            const parent = /** @type {HTMLElement} */ ( parentRef )
            const direction = ( imageRef.naturalWidth / imageRef.naturalHeight ) > ( parent.offsetWidth / parent.offsetHeight ) ? 'x' : 'y'
            const scaleFactor = direction == 'x' ? imageRef.clientHeight / imageRef.naturalHeight : imageRef.clientWidth / imageRef.naturalWidth

            let fullLimit
            
            // set parallax direction and limit
            if ( direction == 'x' ) {
                fullLimit = ( imageRef.naturalWidth * scaleFactor - parent.offsetWidth ) / 2

                context.direction = direction
            } else {
                fullLimit = ( imageRef.naturalHeight * scaleFactor - parent.offsetHeight ) / 2
            }

            context.limitFactor = Math.min( parseInt( context.limit ) / fullLimit, 1.0 )
        }
    }
} )